// src/components/StayInTouch.js
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import memberstackDOM from "@memberstack/dom";
import { getMemberstackKey } from "../utils";
import { standardUserApi } from "../services/standardUserApi";
import useNoIndex from "../hooks/useNoIndex";
import LoadingState from "./LoadingState";
import { colors, type, radii, shadow, cardStyle, buttonPrimary, primaryHover } from "../styles/designTokens";

const currentKey = getMemberstackKey();
const memberstack = memberstackDOM.init({ publicKey: currentKey });

const CADENCE_OPTIONS = [
  { label: "Weekly", days: 7 },
  { label: "Every 2 weeks", days: 14 },
  { label: "Monthly", days: 30 },
  { label: "Every 3 months", days: 90 },
];

const StayInTouch = () => {
  const navigate = useNavigate();
  useNoIndex();

  const [isVerifyingAuth, setIsVerifyingAuth] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [connections, setConnections] = useState([]);
  const [circles, setCircles] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);

  const [newTargetKey, setNewTargetKey] = useState("");
  const [newCadenceDays, setNewCadenceDays] = useState(14);

  useEffect(() => {
    const validateAndLoad = async () => {
      const token = localStorage.getItem("memberstackToken");
      if (!token) {
        setShowLoginModal(true);
        setIsVerifyingAuth(false);
        return;
      }

      let currentMember = null;
      try {
        currentMember = await memberstack.getCurrentMember();
      } catch (err) {
        console.error("StayInTouch › getCurrentMember error:", err);
      }

      if (!currentMember || !currentMember.data) {
        setShowLoginModal(true);
        setIsVerifyingAuth(false);
        return;
      }

      const hasValidStatus = (currentMember.data.planConnections || []).some((plan) => {
        const status = plan.status?.toLowerCase();
        const type = plan.type?.toLowerCase();
        return type === "subscription" && ["active", "trialing", "cancelled", "canceled"].includes(status);
      });

      setIsVerifyingAuth(false);

      if (!hasValidStatus) {
        navigate("/checkout");
        return;
      }

      await loadData(token);
      setLoading(false);
    };

    validateAndLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const loadData = async (token) => {
    try {
      const [remindersData, connectionsData, circlesData] = await Promise.all([
        standardUserApi.getCadenceReminders(token),
        standardUserApi.getConnections(token),
        standardUserApi.getCircles(token),
      ]);
      setReminders(remindersData);
      setConnections(connectionsData.filter((c) => c.status === "accepted"));
      setCircles(circlesData);
    } catch (err) {
      console.error("StayInTouch › load error:", err);
      setErrorMessage("Couldn't load your Stay In Touch list right now. Please try again shortly.");
    }
  };

  const handleAddCadence = async (e) => {
    e.preventDefault();
    if (!newTargetKey) return;
    const [type, id] = newTargetKey.split(":");
    const token = localStorage.getItem("memberstackToken");
    try {
      await standardUserApi.setCadenceReminder(token, {
        connectionId: type === "connection" ? Number(id) : undefined,
        circleId: type === "circle" ? Number(id) : undefined,
        cadenceDays: Number(newCadenceDays),
      });
      setNewTargetKey("");
      await loadData(token);
    } catch (err) {
      console.error("StayInTouch › add error:", err);
      setErrorMessage("Couldn't save that right now. Please try again shortly.");
    }
  };

  const handleMarkCaughtUp = async (reminderId) => {
    const token = localStorage.getItem("memberstackToken");
    try {
      await standardUserApi.markCadenceReminderCaughtUp(token, reminderId);
      await loadData(token);
    } catch (err) {
      console.error("StayInTouch › mark caught up error:", err);
      setErrorMessage("Couldn't update that right now. Please try again shortly.");
    }
  };

  const handleDelete = async (reminderId) => {
    const token = localStorage.getItem("memberstackToken");
    try {
      await standardUserApi.deleteCadenceReminder(token, reminderId);
      await loadData(token);
    } catch (err) {
      console.error("StayInTouch › delete error:", err);
      setErrorMessage("Couldn't remove that right now. Please try again shortly.");
    }
  };

  if (isVerifyingAuth || loading) {
    return <LoadingState />;
  }

  if (showLoginModal) {
    return (
      <div style={{ textAlign: "center", padding: "80px 20px" }}>
        <p style={{ color: colors.body, marginBottom: "16px" }}>Please log in to view Stay In Touch.</p>
        <Link to="/login" style={{ color: colors.primary, fontWeight: "700" }}>Log in</Link>
      </div>
    );
  }

  // Targets that don't already have a cadence set, for the "add" dropdown.
  const usedConnectionIds = new Set(reminders.filter((r) => r.connectionId).map((r) => r.connectionId));
  const usedCircleIds = new Set(reminders.filter((r) => r.circleId).map((r) => r.circleId));
  const availableConnections = connections.filter((c) => !usedConnectionIds.has(c.id));
  const availableCircles = circles.filter((c) => !usedCircleIds.has(c.id));
  const overdueCount = reminders.filter((r) => r.isOverdue).length;

  return (
    <>
      <div style={{ background: colors.gradientTint, borderBottom: `1px solid ${colors.primaryTintBorder}` }}>
        <div style={{ maxWidth: "640px", margin: "0 auto", padding: "48px 20px 36px", textAlign: "center" }}>
          <p style={type.eyebrow}>Never lose touch</p>
          <h1 style={{ ...type.h1, marginTop: "10px", marginBottom: "10px" }}>Stay In Touch</h1>
          <p style={{ ...type.body, maxWidth: "440px", margin: "0 auto 20px" }}>
            Set how often you want to catch up with a connection or circle — we'll nudge you before
            it quietly slips.
          </p>
          {overdueCount > 0 && (
            <div style={{
              display: "inline-flex", alignItems: "center", gap: "8px", background: colors.warningTint,
              border: `1px solid ${colors.warningBorder}`, borderRadius: radii.pill, padding: "8px 18px",
            }}>
              <span style={{ fontWeight: 800, color: colors.warning, fontSize: "1rem" }}>{overdueCount}</span>
              <span style={{ color: colors.warning, fontWeight: 600, fontSize: "0.85rem" }}>
                {overdueCount === 1 ? "catch-up is overdue" : "catch-ups are overdue"}
              </span>
            </div>
          )}
        </div>
      </div>

      <div style={{ maxWidth: "640px", margin: "0 auto", padding: "36px 20px 64px" }}>
        {errorMessage && <p style={{ color: colors.danger, textAlign: "center", marginBottom: "16px", fontSize: "0.9rem" }}>{errorMessage}</p>}

        {reminders.length === 0 ? (
          <div style={{ ...cardStyle, textAlign: "center", padding: "32px 24px", marginBottom: "24px" }}>
            <p style={{ fontSize: "1.6rem", marginBottom: "10px" }} aria-hidden="true">⏰</p>
            <p style={{ color: colors.ink, fontWeight: 700, fontSize: "0.98rem", marginBottom: "6px" }}>Nothing set up yet</p>
            <p style={{ color: colors.muted, fontSize: "0.9rem", lineHeight: 1.55 }}>Add a connection or circle below to get started.</p>
          </div>
        ) : (
          reminders.map((r) => (
            <div
              key={r.id}
              style={{
                border: `1px solid ${r.isOverdue ? colors.warningBorder : colors.border}`,
                background: r.isOverdue ? colors.warningTint : colors.surface,
                borderRadius: radii.md,
                padding: "16px 20px",
                marginBottom: "10px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "12px",
                boxShadow: r.isOverdue ? "none" : shadow.card,
              }}
            >
              <div>
                <div style={{ color: colors.ink, fontWeight: "700", fontSize: "0.98rem" }}>{r.label || "Unknown"}</div>
                <div style={{ color: r.isOverdue ? colors.warning : colors.muted, fontSize: "0.85rem", marginTop: "2px" }}>
                  {r.isOverdue ? "⏰ Overdue to catch up — " : ""}
                  Every {r.cadenceDays} days
                  {r.lastContactAt ? ` · last caught up ${new Date(r.lastContactAt).toLocaleDateString()}` : ""}
                </div>
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={() => handleMarkCaughtUp(r.id)}
                  style={{ ...buttonPrimary, padding: "8px 16px", fontSize: "0.82rem", boxShadow: "none" }}
                  {...primaryHover}
                >
                  Mark caught up
                </button>
                <button
                  onClick={() => handleDelete(r.id)}
                  style={{ backgroundColor: "transparent", color: colors.faint, border: `1.5px solid ${colors.border}`, borderRadius: radii.pill, padding: "8px 16px", fontWeight: "600", cursor: "pointer", fontSize: "0.82rem" }}
                >
                  Remove
                </button>
              </div>
            </div>
          ))
        )}

        {(availableConnections.length > 0 || availableCircles.length > 0) && (
          <form onSubmit={handleAddCadence} style={{ ...cardStyle, display: "flex", gap: "10px", marginTop: "20px", flexWrap: "wrap" }}>
            <select
              value={newTargetKey}
              onChange={(e) => setNewTargetKey(e.target.value)}
              style={{ flex: "1 1 200px", padding: "10px 14px", borderRadius: radii.sm, border: `1.5px solid ${colors.border}`, fontSize: "0.9rem" }}
            >
              <option value="">Choose a connection or circle...</option>
              {availableConnections.map((c) => (
                <option key={`connection:${c.id}`} value={`connection:${c.id}`}>
                  {c.otherUser.firstName} {c.otherUser.lastName}
                </option>
              ))}
              {availableCircles.map((c) => (
                <option key={`circle:${c.id}`} value={`circle:${c.id}`}>{c.name} (circle)</option>
              ))}
            </select>
            <select
              value={newCadenceDays}
              onChange={(e) => setNewCadenceDays(e.target.value)}
              style={{ padding: "10px 14px", borderRadius: radii.sm, border: `1.5px solid ${colors.border}`, fontSize: "0.9rem" }}
            >
              {CADENCE_OPTIONS.map((opt) => (
                <option key={opt.days} value={opt.days}>{opt.label}</option>
              ))}
            </select>
            <button
              type="submit"
              disabled={!newTargetKey}
              style={{ ...buttonPrimary, padding: "10px 22px", fontSize: "0.9rem", cursor: newTargetKey ? "pointer" : "default", opacity: newTargetKey ? 1 : 0.6 }}
              onMouseEnter={newTargetKey ? primaryHover.onMouseEnter : undefined}
              onMouseLeave={newTargetKey ? primaryHover.onMouseLeave : undefined}
            >
              Add
            </button>
          </form>
        )}

        {connections.length === 0 && circles.length === 0 && (
          <p style={{ color: colors.faint, textAlign: "center", marginTop: "20px", fontSize: "0.9rem" }}>
            You'll need at least one connection or circle first — see{" "}
            <Link to="/connections" style={{ color: colors.primary, fontWeight: 600 }}>My Connections</Link>.
          </p>
        )}
      </div>
    </>
  );
};

export default StayInTouch;
